import React, { useContext, useEffect, useState } from "react";
import { Spinner } from "reactstrap";
import { Box, Button, IconButton, Typography } from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import TextField from "@mui/material/TextField";
import { UsernameSelectField } from "../../pages/MyTracks/styles";
import { EmailInviteTextField, useStyles } from "../../pages/MyTracks/styles";
import { styles } from "../../pages/MyTracks/styles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { LightTooltip } from "../LightToolTip/LightToolTip";
import { batchTrackCollabDetailsStyles } from "./styles";
import { BatchTrackModalCollabDetailsProps } from "./types";
import { FONT_FAMILY, isEnterprise } from "../../theme";
import {
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { getEnterpriseAllUsers } from "../../services/enterpriseService";
import { GetEnterpriseAllUsersResponse } from "../../proto/api_pb";

export const BatchTrackModalCollabDetails: React.FC<
  BatchTrackModalCollabDetailsProps
> = ({
  showCollabTooltip,
  handleShowCollabTooltip,
  setShowCollabTooltip,
  collabVal,
  setCollabVal,
  usersList,
  collaborators,
  isSubmitting,
  currentUpload,
  values,
  removeCollaborator,
  addCollaborator,
  emailInvite,
  handleChangeInvites,
  handleCollabInvites,
  handleRemoveEmail,
  emailInviteList,
  handleClose,
}) => {
  const [userNames, setUserNames] = useState<string[]>([]);
  const { isEnterpriseAccount, enterprise, userName } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const classes = useStyles();
  const { getValidAccessToken } = useGetValidAccessToken();
  useEffect(() => {
    const handleUserNames = async () => {
      const token = await getValidAccessToken();
      const res = await getEnterpriseAllUsers(token, enterprise?.id ?? "");
      if (res instanceof GetEnterpriseAllUsersResponse) {
        const uName = res
          .getUsersList()
          .filter((user) => !user.getUsername()?.includes("dummy_ommuse"))
          .map((user) => user.getUsername() || user.getEmail());
        setUserNames(uName);
      }
    };
    handleUserNames();
  }, [enterprise?.id, getValidAccessToken]);

  return (
    <>
      <Box className="collab-label">
        <span>Add Collaborators </span>
        <span className="optional-input">(optional)</span>
        <Box
          style={{ display: "inline" }}
          onMouseOver={() => setShowCollabTooltip(true)}
          onMouseLeave={() => setShowCollabTooltip(false)}
        >
          <LightTooltip
            title="You can add artists, composers, and more collaborators"
            placement="top"
            open={showCollabTooltip}
            onClick={handleShowCollabTooltip}
          >
            <IconButton disableRipple>
              <HelpOutlineIcon
                color="info"
                style={batchTrackCollabDetailsStyles.helpOutlineIcon}
              />
            </IconButton>
          </LightTooltip>
        </Box>
      </Box>
      <hr />

      <Box sx={batchTrackCollabDetailsStyles.addCreatorsContainer}>
        <Box sx={{ width: "80%" }}>
          <label
            htmlFor="newCollaboratorUser"
            style={styles.modalCreatorUsername}
          >
            {"Creator's Username"}
          </label>
          <UsernameSelectField
            freeSolo
            forcePopupIcon={false}
            isOptionEqualToValue={(option, value) => option === value}
            inputValue={collabVal}
            onInputChange={(
              event: React.SyntheticEvent<Element, Event>,
              value: string
            ) => {
              setCollabVal(value);
            }}
            options={
              isEnterpriseAccount
                ? userNames.filter((name) => name !== userName)
                : usersList
                    .filter((item) => item.getUsername() !== userName)
                    .map((item) => item.getUsername())
                    .sort()
            }
            renderInput={(params) => (
              <TextField
                style={batchTrackCollabDetailsStyles.userNameTextField}
                {...params}
                label=" "
                InputLabelProps={{
                  shrink: false,
                }}
                classes={{ root: classes.customTextField }}
              />
            )}
            renderOption={(props, option: any) => (
              <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                {option}
              </li>
            )}
          />
        </Box>
        <Button
          disabled={collabVal === ""}
          sx={batchTrackCollabDetailsStyles.addButton}
          size="small"
          variant="contained"
          onClick={() => {
            if (!isSubmitting) {
              addCollaborator(collabVal);
            }
          }}
        >
          Add
        </Button>
      </Box>
      <Box
        className="collaborators"
        style={batchTrackCollabDetailsStyles.collaboratorsBox}
      >
        <Box className="collaborators-target">
          {collaborators &&
            collaborators.length > 0 &&
            collaborators.map((collaborator, index) => (
              <Box className="collaborator" key={index}>
                <p style={batchTrackCollabDetailsStyles.collaboratorUserName}>
                  {collaborator.getUsername()}
                </p>
                {!isSubmitting && (
                  <IconButton onClick={() => removeCollaborator(collaborator)}>
                    <DeleteForeverIcon />
                  </IconButton>
                )}
              </Box>
            ))}
        </Box>
      </Box>
      {!isEnterprise && (
        <Box sx={batchTrackCollabDetailsStyles.inviteEmailContainer}>
          <Box sx={{ width: "80%" }}>
            <label htmlFor="emailInvite" style={styles.modalCreatorUsername}>
              {"Invite Collaborator"}
            </label>
            <EmailInviteTextField
              hiddenLabel
              variant="outlined"
              classes={{ root: classes.customTextField }}
              placeholder="Send an invite email to a collaborator"
              value={emailInvite}
              onChange={handleChangeInvites}
            />
          </Box>
          <Button
            disabled={emailInvite === ""}
            sx={batchTrackCollabDetailsStyles.addButton}
            size="small"
            variant="contained"
            onClick={handleCollabInvites}
          >
            Add
          </Button>
        </Box>
      )}

      <Box style={batchTrackCollabDetailsStyles.deleteBox}>
        <Box>
          {emailInviteList &&
            emailInviteList.length > 0 &&
            emailInviteList.map((email, index) => (
              <Box
                className="collaborator"
                style={{ width: "100%" }}
                key={index}
              >
                <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1 }}>{email}</p>

                <IconButton onClick={() => handleRemoveEmail(email)}>
                  <DeleteForeverIcon />
                </IconButton>
              </Box>
            ))}
        </Box>
      </Box>
      <Box sx={batchTrackCollabDetailsStyles.buttonBox}>
        <Button
          disabled={isSubmitting}
          fullWidth
          type="submit"
          sx={styles.modalSubmitButton}
          size="small"
          variant="contained"
        >
          {isSubmitting ? (
            <>
              <Spinner
                as="span"
                animation="grow"
                size="sm"
                role="status"
                aria-hidden="true"
                color="black"
              />{" "}
              <span style={batchTrackCollabDetailsStyles.uploadingSpan}>
                {currentUpload ? `Uploading ${currentUpload}` : `Uploading...`}
              </span>
            </>
          ) : (
            <Typography variant="h1" sx={styles.modalSubmitButtonText}>
              Add Tracks
            </Typography>
          )}
        </Button>
        <Button
          onClick={() => handleClose()}
          sx={{
            ...styles.skipButton,
            ...{ marginRight: "15px" },
          }}
        >
          Cancel
        </Button>
      </Box>
    </>
  );
};
